import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { brokerageAPI } from '../../services/api';
import SuccessModal from '../Common/SuccessModal';
import ErrorModal from '../Common/ErrorModal';
import './BrokerageCreate.css';

const BrokerageCreate = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    unitLocation: '',
    ownerName: '',
    commissionAmount: '',
    brokerName: '',
    notes: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.date) {
      newErrors.date = t('brokerage.create.errors.dateRequired');
    }
    if (!formData.unitLocation.trim()) {
      newErrors.unitLocation = t('brokerage.create.errors.unitLocationRequired');
    }
    if (!formData.ownerName.trim()) {
      newErrors.ownerName = t('brokerage.create.errors.ownerNameRequired');
    }
    if (!formData.commissionAmount || parseFloat(formData.commissionAmount) <= 0) {
      newErrors.commissionAmount = t('brokerage.create.errors.commissionAmountRequired');
    }
    if (!formData.brokerName.trim()) {
      newErrors.brokerName = t('brokerage.create.errors.brokerNameRequired');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        date: new Date(formData.date).toISOString(),
        commissionAmount: parseFloat(formData.commissionAmount),
      };
      await brokerageAPI.create(submitData);
      setShowSuccessModal(true);
      setTimeout(() => {
        navigate('/brokerage');
      }, 1000);
    } catch (err) {
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      } else {
        setErrorMessage(t('brokerage.create.messages.createError'));
        setShowErrorModal(true);
      }
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-title">
          <i className="fas fa-plus-circle page-icon"></i>
          <h1>{t('brokerage.create.title')}</h1>
        </div>
        <button className="btn-back" onClick={() => navigate('/brokerage')}>
          <i className="fas fa-arrow-left"></i> {t('brokerage.create.backToList')}
        </button>
      </div>

      <div className="form-container">
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-calendar"></i> {t('brokerage.create.fields.date')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-calendar input-icon"></i>
                <input
                  type="date"
                  className={`form-input ${errors.date ? 'is-invalid' : ''}`}
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                />
              </div>
              {errors.date && (
                <div className="error-message">{errors.date}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-dollar-sign"></i> {t('brokerage.create.fields.commissionAmount')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-dollar-sign input-icon"></i>
                <input
                  type="number"
                  step="0.01"
                  className={`form-input ${errors.commissionAmount ? 'is-invalid' : ''}`}
                  name="commissionAmount"
                  value={formData.commissionAmount}
                  onChange={handleChange}
                  placeholder={t('brokerage.create.placeholders.commissionAmount')}
                />
              </div>
              {errors.commissionAmount && (
                <div className="error-message">{errors.commissionAmount}</div>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-map-marker-alt"></i> {t('brokerage.create.fields.unitLocation')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-map-marker-alt input-icon"></i>
                <input
                  type="text"
                  className={`form-input ${errors.unitLocation ? 'is-invalid' : ''}`}
                  name="unitLocation"
                  value={formData.unitLocation}
                  onChange={handleChange}
                  placeholder={t('brokerage.create.placeholders.unitLocation')}
                  maxLength={500}
                />
              </div>
              {errors.unitLocation && (
                <div className="error-message">{errors.unitLocation}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-user"></i> {t('brokerage.create.fields.ownerName')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-user input-icon"></i>
                <input
                  type="text"
                  className={`form-input ${errors.ownerName ? 'is-invalid' : ''}`}
                  name="ownerName"
                  value={formData.ownerName}
                  onChange={handleChange}
                  placeholder={t('brokerage.create.placeholders.ownerName')}
                  maxLength={255}
                />
              </div>
              {errors.ownerName && (
                <div className="error-message">{errors.ownerName}</div>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-handshake"></i> {t('brokerage.create.fields.brokerName')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-handshake input-icon"></i>
                <input
                  type="text"
                  className={`form-input ${errors.brokerName ? 'is-invalid' : ''}`}
                  name="brokerName"
                  value={formData.brokerName}
                  onChange={handleChange}
                  placeholder={t('brokerage.create.placeholders.brokerName')}
                  maxLength={255}
                />
              </div>
              {errors.brokerName && (
                <div className="error-message">{errors.brokerName}</div>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group form-group-full">
              <label className="form-label">
                <i className="fas fa-align-left"></i> {t('brokerage.create.fields.notes')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-align-left input-icon"></i>
                <textarea
                  className="form-input"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder={t('brokerage.create.placeholders.notes')}
                  rows="3"
                />
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-cancel"
              onClick={() => navigate('/brokerage')}
            >
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? t('brokerage.create.saving') : t('brokerage.create.save')}
            </button>
          </div>
        </form>
      </div>

      <SuccessModal
        show={showSuccessModal}
        message={t('brokerage.create.messages.createSuccess')}
        onClose={() => {
          setShowSuccessModal(false);
        }}
      />

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
    </div>
  );
};

export default BrokerageCreate;

